import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useQueryClient } from '@tanstack/react-query'
import useModals from 'src/Hooks/useModals'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { smartMeterGatewayQueryKey, useFetchSmartMeterGateway } from './use-fetch-smart-meter-getaway'
import { useLatestState } from '../../hooks'
import { toggleSmartMeterGatewayActivation, updateSmartMeterGatewayPairingId } from '../api/smart-meter.api'
import { SmartMeterGatewayDto, syncInstallation } from 'src/Libs/flow/installation/api'
import { errorFormatter } from 'src/Libs/flow/parsers'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { usePortalMutation } from 'src/Libs/api'

type UseSmartMeterGatewayOptions = {
  installationId: number
  emcDeviceId: string
  emcIsPaired: boolean
}
interface ReturnType {
  data: SmartMeterGatewayDto | undefined
  isSmartMeterLoading: boolean
  isLoading: boolean
  isError: boolean
  isActive: boolean
  toggleIsActive: () => void
  isEmpty: boolean
  isPairingDisabled: boolean
  handlePairingClick: () => void
}

/**
 * Hook for managing Smart Meter Gateway state
 */
export const useSmartMeterGateway = ({ installationId, emcDeviceId, emcIsPaired }: UseSmartMeterGatewayOptions): ReturnType => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const { getValues } = useInstallationForm()
  const { enqueueErrorStack } = useSnackbar()

  const {
    data,
    isLoading: isSmartMeterLoading,
    isError,
    isPending: isSmartMeterPending,
    refetch,
  } = useFetchSmartMeterGateway(installationId ?? -1, emcDeviceId)

  const { openSmartMeterGatewayPairingDialog, openInstallationPairingDialog } = useModals()

  const isActive = !!data?.smartMeterGateway.active

  const isNew = data === null
  const [localIsActive, setLocalIsActive] = useLatestState(!!data?.smartMeterGateway.active)

  const { mutate, isPending } = usePortalMutation({
    mutationFn: async (active: boolean) => {
      const result = await toggleSmartMeterGatewayActivation(installationId, emcDeviceId, isNew, active, authFetch)
      if (emcIsPaired) await syncInstallation(installationId, authFetch)
      return result
    },
    onMutate: active => setLocalIsActive(active),
    onError: (error, active) => {
      enqueueErrorStack(
        t('installation.smartMeterGateway.active.error', '', {
          error: errorFormatter(error),
        })
      )
      setLocalIsActive(!active)
    },
    onSuccess: () => refetch(),
  })

  const isLoading = isPending || isSmartMeterPending

  const toggleIsActive = (): void => {
    if (isLoading) return
    mutate(!localIsActive)
  }

  const isEmpty = !data?.smartMeterGateway?.ski || (data?.smartMeterGateway && !data?.smartMeterGateway.ski?.length)

  const queryClient = useQueryClient()

  const isPairingDisabled = !data?.smartMeterGateway?.active || !canEdit

  const handlePairingClick = (): void => {
    if (isPairingDisabled) return

    openSmartMeterGatewayPairingDialog({
      installationId,
      emc: getValues('emcDevice'),
      currentSki: data?.smartMeterGateway?.ski ?? '',
      submitPairingId: pairingId => {
        openInstallationPairingDialog({
          installationId,
          message: t('installation.save.deviceLinkPairingSaving'),
          request: () => updateSmartMeterGatewayPairingId(installationId, emcDeviceId, pairingId, authFetch),
          onOperationFinished: () => {
            queryClient.invalidateQueries({
              queryKey: smartMeterGatewayQueryKey(installationId, emcDeviceId),
            })
          },
        })
      },
    })
  }

  return {
    data,
    isSmartMeterLoading,
    isLoading,
    isError,
    isActive,
    toggleIsActive,
    isEmpty,
    isPairingDisabled,
    handlePairingClick,
  }
}
